<template>
  <div class="ima-sign-list">

    <ima-loading-spinner v-if="isLoading"></ima-loading-spinner>

    <div v-else>
      <h1 class="title">Verfügbare Schilder ({{signs.data.length}})</h1>

      <ima-filter-toolbar :activeFilters="activeFilters">

        <b-field>
          <b-input placeholder="Suchen" type="search" icon="search" v-model="filter.term.value"></b-input>
        </b-field>

        <b-field>
          <b-dropdown @change="(value)=>addFilter('types', value)">
            <button class="button" slot="trigger">
              <b-icon icon="web" size="is-small"></b-icon>
              <span>Typ</span>
              <b-icon icon="arrow_drop_down"></b-icon>
            </button>
            <b-dropdown-item v-for="option in options.types" :value="option" :key="option.id">{{option.label}}</b-dropdown-item>
          </b-dropdown>
        </b-field>

        <b-field>
          <b-dropdown @change="(value) => addFilter('status', value)">
            <button class="button" slot="trigger">
              <b-icon icon="fiber_manual_record" size="is-small"></b-icon>
              <span>Status</span>
              <b-icon icon="arrow_drop_down"></b-icon>
            </button>
            <b-dropdown-item v-for="option in options.status" :value="option" :key="option.id">{{option.label}}</b-dropdown-item>
          </b-dropdown>
        </b-field>

        <b-field>
          <b-dropdown @change="(value) => addFilter('exhibitions', value)">
            <button class="button" slot="trigger">
              <b-icon icon="date_range" size="is-small"></b-icon>
              <span>Messe</span>
              <b-icon icon="arrow_drop_down"></b-icon>
            </button>
            <b-dropdown-item v-for="option in options.exhibitions" :value="option" :key="option.id">{{option.label}}</b-dropdown-item>
          </b-dropdown>
        </b-field>

        <b-switch v-if="canArchiveSigns" :value="this.filter.archived.isActive" @input="toggleFilter('archived')">Archivierte ausblenden</b-switch>

      </ima-filter-toolbar>

      <b-table class="ima-signs-list__table" :data="filteredData" :checked-rows.sync="selected" :striped="true" :checkable="true" :loading="signs.isLoading" :mobile-cards="true" :paginated="true" :per-page="25" :pagination-simple="true" default-sort="['id']">

        <template slot-scope="props">
          <b-table-column field="id" label="ID" sortable numeric>
            {{ props.row.id }}
          </b-table-column>
          <b-table-column field="thumbnail" label="Vorschau">
            <router-link :to="{ name: routes.SIGN_DETAILS, params: { id: props.row.id}}">
              <ima-sign-thumbnail :sign="props.row" />
            </router-link>
          </b-table-column>
          <b-table-column field="name" label="Bezeichnung" sortable>
            {{ props.row.name }}
          </b-table-column>

          <b-table-column field="name" label="Status" sortable>
            <ima-sign-status-label :status="props.row.status" />
          </b-table-column>

          <b-table-column field="variants" label="Varianten">
            <span v-for="variant in props.row.variants" :key="variant.id" class="tag is-light">
              {{variant.name}}&nbsp;&nbsp;
              <b-tooltip v-if="variant.isPublished" label="Veröffentlicht">
                <b-icon icon="check_circle" size="is-small" />
              </b-tooltip>

            </span>
          </b-table-column>

          <b-table-column field="signType" label="Typ" sortable>
            {{ props.row.signType.name }}
          </b-table-column>
          <b-table-column field="exhibition" label="Messe" sortable>
            {{ props.row.exhibition.name }}
          </b-table-column>

          <b-table-column field="createdAt" label="Erstellt" class="nowrap">
            {{props.row.createdAt | moment('DD.MM.YYYY HH:mm')}}
          </b-table-column>

          <b-table-column field="createdBy" label="Erstellt von" class="nowrap">
            {{props.row.createdBy.firstName}} {{props.row.createdBy.lastName}}
          </b-table-column>

          <b-table-column field="updatedAt" label="Zuletzt bearbeitet" class="nowrap">
            {{props.row.updatedAt | moment('DD.MM.YYYY HH:mm')}}
          </b-table-column>

          <b-table-column field="updatedBy" label="Bearbeitet von" class="nowrap">
            <span v-if="props.row.updatedBy">{{props.row.updatedBy.firstName}} {{props.row.updatedBy.lastName}}</span>
          </b-table-column>

          <b-table-column field="isArchived" label="Archiviert" sortable v-if="canArchiveSigns">
            <ima-sign-archive-button :sign="props.row" />
          </b-table-column>

          <b-table-column field="actions" label=" ">
            <div class="control">
              <router-link :to="{ name: routes.SIGN_DETAILS, params: { id: props.row.id}}" class="button round-button is-primary">
                <b-icon icon="arrow_forward"></b-icon>
              </router-link>
              <button v-if="canAddSignsToShoppingCart" class="button round-button is-primary is-inverted" :disabled="!isSignFinished(props.row)" type="button" @click="onSignAddToShoppingCart(props.row)">
                <span class="icon">
                  <b-icon icon="add_shopping_cart" />
                </span>
              </button>

              <button v-if="canCopySigns" class="button round-button is-warning" type="button" @click="onSignCopy(props.row)">
                <span class="icon">
                  <b-icon icon="content_copy" />
                </span>
              </button>

              <button v-if="canDeleteSign(props.row)" class="button round-button is-danger" type="button" @click="onSignRemove(props.row.id)">
                <span class="icon">
                  <b-icon icon="delete" />
                </span>
              </button>
            </div>
          </b-table-column>

        </template>

        <div slot="empty">
          Keine Schilder verfügbar
        </div>
      </b-table>

      <button v-if="canCreateSigns" class="button floating-button" @click="openSignModal()">
        <b-icon icon="add"></b-icon>
      </button>

    </div>

  </div>
</template>


<script>
import { uniqBy, sortBy } from 'lodash'
import { mapState, mapActions, mapMutations } from 'vuex'
import filterListing from '@/mixins/filter-listing'
import routes from '@/mixins/routes'
import { selectSign } from '@/store/modules/shopping-cart'
import { removeSign } from '@/store/modules/sign'
import { fetchSigns, selectSignToCopy } from '@/store/modules/signs'
import { fetchExhibitions } from '@/store/modules/exhibitions'
import {
  openSignModal,
  openAddSignToShoppingCartModal,
  openCopySignModal
} from '@/store/modules/modals'
import LoadingSpinner from '@/components/LoadingSpinner'
import FilterToolbar from '@/components/FilterToolbar'
import SignArchiveButton from '@/components/SignArchiveButton'
import SignStatusLabel from '@/components/SignStatusLabel'
import SignThumbnail from '@/components/SignThumbnail'
import {
  canArchiveSigns,
  canAddSignsToShoppingCart,
  canDeleteSigns,
  canDeleteSpecificSign,
  canCreateSigns,
  canCopySigns
} from '@/services/permissions'

export default {
  name: 'ima-sign-list',
  components: {
    'ima-loading-spinner': LoadingSpinner,
    'ima-filter-toolbar': FilterToolbar,
    'ima-sign-archive-button': SignArchiveButton,
    'ima-sign-status-label': SignStatusLabel,
    'ima-sign-thumbnail': SignThumbnail
  },
  mixins: [routes, filterListing],
  data () {
    return {
      filter: {
        term: {
          value: null,
          filter (sign) {
            return [sign.id, sign.name]
              .join()
              .toLowerCase()
              .includes(this.value.toLowerCase())
          }
        },
        archived: {
          isActive: true,
          filter ({ isArchived }) {
            return !isArchived
          }
        },
        types: {
          label: 'Typ',
          values: [],
          filter (sign) {
            return this.values
              .map(({ value }) => value)
              .includes(sign.signType.id)
          }
        },
        status: {
          label: 'Status',
          values: [],
          filter (sign) {
            return this.values
              .map(({ value }) => value)
              .includes(sign.status.id)
          }
        },
        exhibitions: {
          label: 'Messe',
          values: [],
          filter (sign) {
            return this.values
              .map(({ value }) => value)
              .includes(sign.exhibition.id)
          }
        }
      },
      selected: []
    }
  },
  methods: {
    ...mapMutations([
      openSignModal,
      openAddSignToShoppingCartModal,
      openCopySignModal,
      selectSign,
      selectSignToCopy
    ]),
    ...mapActions([fetchSigns, removeSign, fetchExhibitions]),
    isSignFinished (sign) {
      return sign.status.name === 'ABGESCHLOSSEN'
    },
    onSignCopy (sign) {
      this.selectSignToCopy(sign)
      this.openCopySignModal()
    },
    onSignAddToShoppingCart (sign) {
      this.selectSign(sign)
      this.openAddSignToShoppingCartModal()
    },
    onSignRemove (id) {
      const sign = this.signs.data.find(sign => sign.id === id)
      this.$dialog.confirm({
        title: 'Schild löschen',
        message: `Sind Sie sicher dass Sie das Schild <strong>${
          sign.name
        }</strong> unwiderruflich <strong>löschen</strong> wollen?`,
        confirmText: 'Schild löschen',
        cancelText: 'Abbrechen',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => {
          this.removeSign(sign)
        }
      })
    },
    canDeleteSign (sign) {
      return canDeleteSigns() || canDeleteSpecificSign(sign)
    }
  },
  computed: {
    ...mapState(['user', 'signs', 'exhibitions']),
    canAddSignsToShoppingCart () {
      return canAddSignsToShoppingCart()
    },
    canArchiveSigns () {
      return canArchiveSigns()
    },
    isLoading () {
      return this.signs.isLoading || this.exhibitions.isLoading
    },
    options () {
      return {
        status: uniqBy(
          this.signs.data.map(({ status }) => ({
            value: status.id,
            label: status.name
          })),
          'value'
        ),
        types: sortBy(
          uniqBy(
            this.signs.data.map(({ signType }) => ({
              value: signType.id,
              label: signType.name
            })),
            'value'
          ),
          'label'
        ),
        exhibitions: sortBy(
          uniqBy(
            this.signs.data.map(({ exhibition }) => ({
              value: exhibition.id,
              label: exhibition.name
            })),
            'value'
          ),
          'label'
        )
      }
    },
    filteredData () {
      return this.filterCollection(this.signs.data)
    },
    canCreateSigns () {
      return canCreateSigns()
    },
    canCopySigns () {
      return canCopySigns()
    }
  },
  created () {
    this.fetchSigns()
    this.fetchExhibitions()
  }
}
</script>


<style lang="scss">
.ima-signs-list {
  &__table {
    tr > td,
    .checkbox-cell {
      vertical-align: middle;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/SignList.vue